import 'react-day-picker/style.css';

import clsx from 'clsx';
import { endOfDay, isToday, startOfDay } from 'date-fns';
import { useId, useMemo } from 'react';
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

import { CalendarIcon } from '@/components/icons/calendar';
import { CloseIcon } from '@/components/icons/close';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { formatShortDate, toDate } from '@/utils/date-util';

import { Calendar } from './calendar';
import { Skeleton } from './skeleton';
import { Typography } from './typography';

interface DatePickerProps {
  id?: string;
  date?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isFetching?: boolean;
  error?: string;
  readOnly?: boolean;
  minDate?: Date;
  isClearable?: boolean;
  maxDate?: Date;
}

export function DatePicker({
  id,
  date,
  onChange,
  placeholder,
  isDisabled,
  isFetching,
  error,
  readOnly,
  minDate,
  isClearable = false,
  maxDate,
}: DatePickerProps) {
  const renderDateContent = () => {
    if (isFetching) return <Skeleton className="h-4 w-2/3" />;

    if (!date) return <span>{placeholder}</span>;

    return formatShortDate(date);
  };

  // Calculate default month: use selected date if available, otherwise use minDate, otherwise use today
  const defaultMonth = useMemo(() => {
    if (date) return date;
    if (minDate) return minDate;
    return new Date();
  }, [date, minDate]);

  return (
    <div className="relative w-full">
      <Popover open={readOnly ? false : undefined}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            disabled={isDisabled}
            className={clsx(
              'w-full justify-start rounded-sm pl-2 font-normal',
              !date && 'text-fx-neutral-400',
              error && 'border-fx-rose',
              readOnly && 'bg-fx-neutral-50 cursor-default',
              isClearable && date && 'pr-8',
            )}
          >
            <CalendarIcon />
            {renderDateContent()}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="pointer-events-auto w-auto p-0"
          align="start"
        >
          <Calendar
            mode="single"
            selected={date}
            onSelect={onChange}
            autoFocus
            disabled={[
              { before: minDate ?? new Date() },
              ...(maxDate ? [{ after: maxDate }] : []),
            ]}
            defaultMonth={defaultMonth}
          />
        </PopoverContent>
      </Popover>
      {isClearable && date && !isDisabled && !readOnly ? (
        <button
          type="button"
          aria-label="Clear date"
          onClick={() => onChange?.(undefined)}
          className="absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center"
        >
          <CloseIcon className="text-fx-neutral-400 hover:text-fx-primary" />
        </button>
      ) : null}
    </div>
  );
}

interface FormDatePickerProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isFetching?: boolean;
  isStartOfDay?: boolean;
  isEndOfDay?: boolean;
  error?: string;
  readOnly?: boolean;
  minDate?: Date;
  maxDate?: Date;
  onChange?: (date: string) => void;
  required?: boolean;
  isClearable?: boolean;
}

// A wrapper over the above DatePicker component to integrate with react-hook-form
export function FormDatePicker<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  isDisabled,
  isFetching,
  isStartOfDay,
  isEndOfDay,
  error,
  readOnly,
  minDate,
  maxDate,
  onChange,
  required,
  isClearable = false,
}: FormDatePickerProps<TFieldValues>) {
  const id = useId();

  const handleChange = (
    field: ControllerRenderProps<TFieldValues, FieldPath<TFieldValues>>,
    date: Date | undefined,
  ) => {
    if (!date) {
      if (isClearable) {
        field.onChange('');
      }
      return;
    }

    let adjusted = date;

    if (isStartOfDay) {
      // If the date is today, set it to the current time
      if (isToday(date)) adjusted = new Date();
      else adjusted = startOfDay(date); // Local start of the selected day
    } else if (isEndOfDay) {
      adjusted = endOfDay(date); // Local end of the selected day
    }

    // ✅ now convert to UTC ISO string
    const isoDate = adjusted.toISOString();
    field.onChange(isoDate);

    // Call optional onChange callback
    if (onChange) {
      onChange(isoDate);
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className="flex w-full flex-col gap-1">
          {label && (
            <label htmlFor={id}>
              <Typography variant="subtitle2">
                {label} {required && <span className="text-fx-rose">*</span>}
              </Typography>
            </label>
          )}
          <DatePicker
            date={field.value ? toDate(field.value as string) : undefined}
            onChange={date => handleChange(field, date)}
            id={id}
            placeholder={placeholder}
            isDisabled={isDisabled}
            isFetching={isFetching}
            error={error}
            readOnly={readOnly}
            minDate={minDate}
            isClearable={isClearable}
            maxDate={maxDate}
          />
          {error && (
            <Typography variant="small1" className="!text-fx-rose">
              {error}
            </Typography>
          )}
        </div>
      )}
    />
  );
}
